import React from 'react';
import { ExternalLink, Upload, X } from 'lucide-react';
import { BrokerGuide } from '../../config/brokerImportGuides';

interface BrokerInstructionsModalProps {
    open: boolean;
    guide: BrokerGuide | null;
    onClose: () => void;
    onBack: () => void;
    onUpload: () => void;
}

export default function BrokerInstructionsModal({ open, guide, onClose, onBack, onUpload }: BrokerInstructionsModalProps) {
    if (!open || !guide) return null;

    return (
        <div className="fixed inset-0 z-[60] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
            <div className="w-full max-w-2xl rounded-2xl border border-white/10 bg-[#12121a] p-6 max-h-[88vh] overflow-y-auto">
                <div className="flex items-start justify-between gap-3 mb-4">
                    <div>
                        <p className="text-xs uppercase tracking-[0.16em] text-indigo-300">How to export from {guide.name}</p>
                        <h3 className="text-xl font-semibold text-white mt-1">{guide.title}</h3>
                    </div>
                    <button onClick={onClose} className="p-2 rounded-lg hover:bg-white/10 text-slate-300" aria-label="Close instructions">
                        <X size={16} />
                    </button>
                </div>

                <div className="space-y-5">
                    {guide.sections.map((section, index) => (
                        <div key={`${guide.id}-section-${index}`}>
                            {section.heading && <h4 className="text-sm font-semibold text-slate-200 mb-2">{section.heading}</h4>}
                            <ol className="list-decimal list-inside space-y-1.5 text-sm text-slate-300">
                                {section.steps.map((step, stepIndex) => (
                                    <li key={`${guide.id}-step-${index}-${stepIndex}`}>{step}</li>
                                ))}
                            </ol>
                        </div>
                    ))}

                    {guide.notes && guide.notes.length > 0 && (
                        <div>
                            <h4 className="text-sm font-semibold text-slate-200 mb-2">Notes</h4>
                            <ul className="list-disc list-inside space-y-1 text-sm text-slate-300">
                                {guide.notes.map((note, index) => (
                                    <li key={`${guide.id}-note-${index}`}>{note}</li>
                                ))}
                            </ul>
                        </div>
                    )}

                    {guide.helpUrl && (
                        <a
                            href={guide.helpUrl}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center gap-1 text-sm text-indigo-300 hover:text-indigo-200"
                        >
                            More info
                            <ExternalLink size={14} />
                        </a>
                    )}
                </div>

                <div className="mt-6 pt-4 border-t border-white/10 flex flex-wrap justify-between gap-2">
                    <button onClick={onBack} className="px-4 py-2 rounded-lg border border-white/10 text-slate-300 hover:bg-white/5">
                        Back
                    </button>
                    <button onClick={onUpload} className="px-4 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-500 text-white inline-flex items-center gap-2">
                        <Upload size={16} />
                        {guide.ctaLabel}
                    </button>
                </div>
            </div>
        </div>
    );
}
